import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, input, signal } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MessageModule } from 'primeng/message';
import { TranslationPipe } from 'src/app/pipes/translation/translation-pipe';

@Component({
  selector: 'app-auto-error',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MessageModule, TranslationPipe],
  templateUrl: './auto-error.html',
  styleUrl: './auto-error.scss'
})
export class AutoError {
  control = input<AbstractControl | null>();
  customErrorMap = input<Record<string, string>>({});
  private dropdownFocused = signal(false);

  constructor(private el: ElementRef) {}

  private errorMap: Record<string, string> = {
    required: 'FIELD_REQUIRED',
    spacesNotAllowed: 'SPACES_NOT_ALLOWED',
    invalidTokens: 'INVALID_TOKENS',
    passwordMismatch: 'PASSWORD_MISMATCH',
    email: 'EMAIL_INVALID',
    pattern: 'PATTERN_INVALID',
    minDate: 'MIN_DATE_INVALID'
  };

  @HostListener('document:mousedown', ['$event.target'])
  onMouseDown(target: EventTarget | null) {
    if (!(target instanceof HTMLElement)) return;
    const container = this.el.nativeElement.parentElement as HTMLElement;
    if (container?.contains(target)) {
      this.dropdownFocused.set(true);
      return;
    }
    const overlay = target.closest<HTMLElement>(
      '.p-overlay, .p-select-overlay, .p-overlay-content, .p-datepicker-panel, .p-multiselect-overlay, .p-autocomplete-overlay'
    );
    if (!overlay) return;
    const input = container?.querySelector<HTMLElement>('[aria-controls]');
    const linkedId = input?.getAttribute('aria-controls');
    if (!linkedId) return;
    const isMatch = overlay.id === linkedId || !!overlay.querySelector(`#${CSS.escape(linkedId)}`);
    if (isMatch) {
      this.dropdownFocused.set(true);
    }
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    if (!this.dropdownFocused()) return;
    setTimeout(() => this.dropdownFocused.set(false), 200);
  }

  get errors(): { key: string; params?: any }[] {
    const control = this.control();
    if (!control || !control.touched || !control.errors) return [];
    if (this.dropdownFocused()) return [];
    if (control.value && !control.dirty) return [];

    const hasValue = control.value !== null && control.value !== undefined && control.value !== '';
    const overrides = this.customErrorMap();

    return Object.keys(control.errors)
      .filter((key) => {
        if (key === 'required') return !hasValue;
        if (key === 'pattern') return hasValue;
        return true;
      })
      .map((errorKey) => {
        const translationKey = overrides[errorKey] || this.errorMap[errorKey] || errorKey;
        const errorValue = control.errors?.[errorKey];
        return {
          key: translationKey,
          params: typeof errorValue === 'object' ? errorValue : undefined
        };
      });
  }
}

export function numberSeriesValidator(extraAllowedTokens: string[] = []): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value || !value.trim()) {
      return null;
    }
    if (/\s/.test(value)) {
      return { spacesNotAllowed: true };
    }
    const tokens = value.match(/\{.*?\}/g) || [];
    const dynamicXPattern = /^\{X+\}$/;
    const invalidTokens = tokens.filter((token: any) => {
      const isDynamicX = dynamicXPattern.test(token);
      const isExtraAllowed = extraAllowedTokens.includes(token);
      return !isDynamicX && !isExtraAllowed;
    });
    if (invalidTokens.length > 0) {
      return {
        invalidTokens: { 0: invalidTokens.join(', ') }
      };
    }
    return null;
  };
}

export function passwordMatchValidator(passwordField: string, confirmField: string): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const password = group.get(passwordField);
    const confirm = group.get(confirmField);
    if (!password || !confirm) return null;
    if (!confirm.value) return null;
    if (password.value !== confirm.value) {
      confirm.setErrors({ ...confirm.errors, passwordMismatch: true });
    } else {
      if (confirm.errors) {
        delete confirm.errors['passwordMismatch'];
        if (!Object.keys(confirm.errors).length) {
          confirm.setErrors(null);
        }
      }
    }
    return null;
  };
}

export function mustBeGreaterValidator(lesserField: string, greaterField: string): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const lesserControl = group.get(lesserField);
    const greaterControl = group.get(greaterField);
    if (!lesserControl || !greaterControl) return null;
    if (greaterControl.value === null || greaterControl.value === undefined || greaterControl.value === '') {
      return null;
    }
    const lesserValue = Number(lesserControl.value);
    const greaterValue = Number(greaterControl.value);
    if (greaterValue <= lesserValue) {
      greaterControl.setErrors({ ...greaterControl.errors, mustBeGreater: true });
    } else if (greaterControl.errors) {
      delete greaterControl.errors['mustBeGreater'];
      if (!Object.keys(greaterControl.errors).length) {
        greaterControl.setErrors(null);
      }
    }
    return null;
  };
}

export function minDateValidator(minDate: Date | (() => Date)): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const min = typeof minDate === 'function' ? minDate() : minDate;
    const value = new Date(control.value);
    value.setHours(0, 0, 0, 0);
    const normalizedMin = new Date(min);
    normalizedMin.setHours(0, 0, 0, 0);
    return value < normalizedMin ? { minDate: { min: normalizedMin } } : null;
  };
}

export function maxDateValidator(maxDate: Date | (() => Date)): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const max = typeof maxDate === 'function' ? maxDate() : maxDate;
    const value = new Date(control.value);
    value.setHours(0, 0, 0, 0);
    const normalizedMax = new Date(max);
    normalizedMax.setHours(0, 0, 0, 0);
    return value > normalizedMax ? { maxDate: { max: normalizedMax } } : null;
  };
}
